import { useEffect, useState } from 'react';
import { Card } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Users, Building2 } from "lucide-react";
import { toast } from "sonner";

interface Department {
  id: string;
  name: string;
  description: string | null;
  parent_department_id: string | null;
}

interface Employee {
  id: string;
  first_name: string;
  last_name: string;
  title: string;
  department: string;
  reports_to: string | null;
  avatar_url: string | null;
}

export const OrgChart = () => {
  const [departments, setDepartments] = useState<Department[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOrgData = async () => {
      try {
        const [deptResult, empResult] = await Promise.all([
          supabase.from('departments').select('*'),
          supabase.from('employee_profiles').select('*')
        ]);

        if (deptResult.error) throw deptResult.error;
        if (empResult.error) throw empResult.error;

        setDepartments(deptResult.data);
        setEmployees(empResult.data);
      } catch (error) {
        console.error('Error fetching org data:', error);
        toast.error('Failed to load organizational data');
      } finally {
        setLoading(false);
      }
    };

    fetchOrgData();
  }, []);

  const getDepartmentEmployees = (deptName: string) => {
    return employees.filter(emp => emp.department === deptName);
  };

  const getManagerName = (reportingId: string | null) => {
    if (!reportingId) return 'None';
    const manager = employees.find(emp => emp.id === reportingId);
    return manager ? `${manager.first_name} ${manager.last_name}` : 'Unknown';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold">Organizational Structure</h2>
      
      <div className="grid gap-6 md:grid-cols-2">
        {departments.map((dept) => (
          <Card key={dept.id} className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-2">
                <Building2 className="h-5 w-5 text-primary" />
                <h3 className="text-xl font-semibold">{dept.name}</h3>
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Users className="h-4 w-4" />
                <span>{getDepartmentEmployees(dept.name).length} members</span>
              </div>
            </div>
            
            {dept.description && (
              <p className="text-sm text-muted-foreground mb-4">{dept.description}</p>
            )}

            <div className="space-y-3">
              {getDepartmentEmployees(dept.name).map((emp) => (
                <div key={emp.id} className="flex items-center justify-between text-sm p-2 rounded-lg bg-secondary/10">
                  <div>
                    <p className="font-medium">{emp.first_name} {emp.last_name}</p>
                    <p className="text-muted-foreground">{emp.title}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-muted-foreground">Reports to</p>
                    <p className="font-medium">{getManagerName(emp.reports_to)}</p>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};